/**
 * Pure helper that mirrors backend `_validate_password` AND
 * `_validate_operator_password`. Kept dependency-free + side-effect-free
 * so it can be unit-tested without DOM / React. UI components import
 * `evaluatePassword(value, opts)` and render the returned booleans.
 *
 * NOTE: This is a UX-side mirror — the backend remains the source of
 * truth for what's actually accepted. Do not gate API calls on the
 * returned `meetsAllRules` flag.
 */

// Defaults match backend tenant policy.
export const MIN_PASSWORD_LEN = 12;
export const REQUIRED_CLASSES = 3;

// Operator-specific defaults — these mirror backend constants
// (OPERATOR_MIN_PASSWORD_LEN, OPERATOR_REQUIRED_CLASSES). Kept here so
// any UI page can render the strict-policy preview before the backend
// policy endpoint returns. The backend remains authoritative.
export const OPERATOR_MIN_PASSWORD_LEN = 20;
export const OPERATOR_REQUIRED_CLASSES = 4;
export const OPERATOR_FORBIDDEN_SUBSTRINGS = [
  "shelflife",
  "shelf-life",
  "console-operator",
  "blueharborlabs",
];

/**
 * Evaluate a password against the configured policy.
 *
 * @param {string} pw
 * @param {{
 *   minLength?: number,
 *   requiredClasses?: number,
 *   forbiddenSubstrings?: string[],
 *   email?: string,
 * }} [opts]
 * @returns {{
 *   length: number,
 *   minLength: number,
 *   requiredClasses: number,
 *   lengthOk: boolean,
 *   hasUpper: boolean,
 *   hasLower: boolean,
 *   hasDigit: boolean,
 *   hasSpecial: boolean,
 *   classCount: 0|1|2|3|4,
 *   classesOk: boolean,
 *   forbiddenHits: string[],
 *   forbiddenOk: boolean,
 *   meetsAllRules: boolean,
 * }}
 */
export function evaluatePassword(pw, opts = {}) {
  const s = typeof pw === "string" ? pw : "";
  const minLength = opts.minLength ?? MIN_PASSWORD_LEN;
  const requiredClasses = opts.requiredClasses ?? REQUIRED_CLASSES;
  const hasUpper = /[A-Z]/.test(s);
  const hasLower = /[a-z]/.test(s);
  const hasDigit = /\d/.test(s);
  const hasSpecial = /[^A-Za-z0-9\s]/.test(s);
  const classCount =
    (hasUpper ? 1 : 0) +
    (hasLower ? 1 : 0) +
    (hasDigit ? 1 : 0) +
    (hasSpecial ? 1 : 0);
  const lengthOk = s.length >= minLength;
  const classesOk = classCount >= requiredClasses;

  // Build the effective forbidden-substring list. Includes the
  // explicit list + (when email provided) local-part, full domain,
  // and the registrable-ish domain root.
  const forbiddenList = (opts.forbiddenSubstrings || []).map((t) =>
    String(t).toLowerCase(),
  );
  if (opts.email && opts.email.includes("@")) {
    const [local, domain] = opts.email.toLowerCase().split("@", 2);
    if (local) forbiddenList.push(local);
    if (domain) {
      forbiddenList.push(domain);
      const root = domain.split(".")[0];
      if (root && !forbiddenList.includes(root)) forbiddenList.push(root);
    }
  }
  const pwLower = s.toLowerCase();
  const forbiddenHits = Array.from(
    new Set(forbiddenList.filter((sub) => sub && pwLower.includes(sub))),
  ).sort();
  const forbiddenOk = forbiddenHits.length === 0;

  return {
    length: s.length,
    minLength,
    requiredClasses,
    lengthOk,
    hasUpper,
    hasLower,
    hasDigit,
    hasSpecial,
    classCount,
    classesOk,
    forbiddenHits,
    forbiddenOk,
    meetsAllRules: lengthOk && classesOk && forbiddenOk,
  };
}
